import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo } from 'react';
import { GameState, GameActions } from '../types/game';
import { useGameState } from '../hooks/useGameState';

interface GameContextType {
  gameState: GameState;
  actions: GameActions;
  showCompletionModal: boolean;
  setShowCompletionModal: (show: boolean) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { gameState, actions } = useGameState();
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  useEffect(() => {
    if (gameState.gameCompleted && !showCompletionModal) {
      // Add a small delay to ensure the DOM is ready
      setShowCompletionModal(true);
    }
  }, [gameState.gameCompleted]);

  const contextValue = useMemo(() => ({
    gameState,
    actions,
    showCompletionModal,
    setShowCompletionModal,
  }), [gameState, actions, showCompletionModal, setShowCompletionModal]);

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};